import React from "react";

/**
 * Plays a discreet metronome click at the given BPM while `active` is true.
 * Independent from the main useMetronome hook so both can coexist.
 *
 * Click pattern: downbeat (1500 Hz, louder) every 4 beats, upbeats (1000 Hz, quieter).
 */
export const useTempoTicker = (bpm, active) => {
  const ctxRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const nextTickRef = React.useRef(0);
  const beatRef = React.useRef(0);

  React.useEffect(() => {
    if (!active || !bpm || bpm <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();

    beatRef.current = 0;
    nextTickRef.current = ctx.currentTime + 0.05;

    const schedule = () => {
      const lookahead = 0.1;
      while (nextTickRef.current < ctx.currentTime + lookahead) {
        const time = nextTickRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const isDown = beatRef.current % 4 === 0;
        osc.frequency.value = isDown ? 1500 : 1000;
        gain.gain.setValueAtTime(0.0001, time);
        gain.gain.exponentialRampToValueAtTime(isDown ? 0.35 : 0.2, time + 0.001);
        gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
        osc.connect(gain).connect(ctx.destination);
        osc.start(time);
        osc.stop(time + 0.06);
        beatRef.current += 1;
        nextTickRef.current += 60 / bpm;
      }
    };

    timerRef.current = setInterval(schedule, 25);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, bpm]);

  // Close audio context on unmount
  React.useEffect(() => {
    return () => {
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (_) { /* ignore */ }
      }
    };
  }, []);
};
